/* eslint-disable max-len */

/**
 * Utility that provides an easy usage for Gen2Migration smart contract.
 * To prevent users from adding wrong arguments, this is a typescript file.
 *
 * <i>Copyright (c) 2019 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module gen2-migration.js
 * @class Gen2Migration
 * @since 0.0.1
*/

const AMOUNT_PER_ADDRESS = 1000,
    errorMessages = require('../constants/error-messages'),
    fileToText = require('../helpers/file-to-text'),
    network = require('../constants/network-info');

let keyParser = require('../helpers/key-parser'), // returns {parse (Function)};
    ecsign = require('../helpers/ecsign'),
    networkAddress = network.networkAddress,
    smartContractAddress = network.smartContractAddress,
    abi = network.abi,
    networkId = network.networkId,
    chainId = network.chainId,
    web3;

const mockedTxPromise = async () => {
    await delay(3000);
    return {
        transactionHash: '0xa9cdffa325d09e99afff708cd918fad4c7b2f2eef0601481971e4e6aa5bb625c',
        transactionIndex: 0,
        blockHash: '0xd0b908a257a47da7ce43376366547a0e04eeb94175060192e2dd471cb6239cee',
        blockNumber: 4,
        from: '0xa2d3cd00f3948dd771afb0ed925e8ecc1fb44628',
        to: '0x5cfd2f091690dc1ec86cb9105a43fb80014e5b74',
        gasUsed: 58062,
        cumulativeGasUsed: 58062,
        contractAddress: null,
        logs: [
            {
                logIndex: 0,
                transactionIndex: 0,
                transactionHash:
                '0xa9cdffa325d09e99afff708cd918fad4c7b2f2eef0601481971e4e6aa5bb625c',
                blockHash:
                '0xd0b908a257a47da7ce43376366547a0e04eeb94175060192e2dd471cb6239cee',
                blockNumber: 4,
                address: '0x5Cfd2f091690dC1EC86cb9105a43Fb80014e5B74',
                data:
                '0x00000000000000000000000000000000000000000000000000000000000000000000000000000000000000003517ce90acf641eab506c13706bb8203f66c149f0000000000000000000000000000000000000000000000056bc75e2d63100000',
                topics: [],
                type: 'mined',
                id: 'log_bd769ff1'
            }
        ],
        status: true,
        logsBloom: '0x00000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000080000000000000000000000000000000000000000000000000000800000000000000000000000000000000000000000000004000000000000000000000000000000002000000000000000000000000000000000000000000000000080000800000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000',
        v: '0x1852d',
        r: '0xef48c5b21203fea3bb28163be27c1f0c1c4129698188a81659da3d7de6e6fe28',
        s: '0x7705e1bb0ff5b20699ca1c2b6e82d15214483d395d4cf8d5fa07c91e914dce4f'
    };
};

/**
 * Claims a Gen2 private key and transfers the NRG balance from the Gen2 Network towards the Gen3 networkAddress.
 *
 * @method claim
 * @return {Object} smart contract response -> the claim is succeeded if response.status===true
 * @since 0.0.1
 */
// @param [energiCommon] {object} not part of public api -> optional energiCommon config, in case already available, so that `claim` doesn't need to ask from the blockchain
const claim = (privateKeyGen2, addressGen3, energiCommon, directResponse) => {
    if (directResponse) {
        return {
            tx: mockedTxPromise()
        };
    }
    return mockedTxPromise();
};

/**
 * Claims multiple Gen2 private keys and transfers the NRG balance from the Gen2 Network towards the Gen3 networkAddress.
 *
 * @method claimMultiple
 * @param multiPrivateKeysGen2 {Array} List with Gen2 private keys, not hexadecimal
 * @return {Promise} Promise holds an array, at each position the smart contract response -> each claim is succeeded if response.status===true, else response.message holds the error.
 * @since 0.0.1
 */
const claimMultiple = async multiPrivateKeysGen2 => {
    if (!Array.isArray(multiPrivateKeysGen2)) {
        return Promise.reject(errorMessages.CLAIM_MULTIPLE_REQUIRES_ARRAY);
    }
    if (multiPrivateKeysGen2.length===0) {
        return Promise.reject(errorMessages.CLAIM_MULTIPLE_EMPTY_ARRAY);
    }
    let txResponses = [],
        claims;
    const total = multiPrivateKeysGen2.length;
    const claimOne = async index => {
        let txResponse;
        try {
            txResponse = await claim(null, null, null, true);
        }
        catch (err) {
            txResponse = {
                tx: Promise.resolve({
                    status: false,
                    message: err.message
                })
            };
        }
        txResponses.push(txResponse);
        index++;
        // If all keys have been processed, then return with `txResponses`,
        // otherwise return with a new promise wich will process the next key:
        return (index<total) ? claimOne(index) : txResponses;
    };

    claims = await claimOne(0);
    // note that `claims` is an array with objects like {tx: Promise}
    // we will return an array with promises, and wait for all items to be resolved
    claims = claims.map(c => c.tx);
    return await Promise.all(claims);
};

/**
 * Claims a Gen2 wallet dump and transfers all the NRG balance from the Gen2 Network towards the Gen3 networkAddress.
 *
 * @method claimWalletDump
 * @param fileBlob {Blob} Fileblob, that comes from an html input[type="file"] Element: listen for the onChange event and retrieve inputnode.files[0] to get the fileBlob.
 * @return {Promise} Promise hold an array, at each position the smart contract response -> each claim is succeeded if response.status===true
 * @since 0.0.1
 */
const claimWalletDump = fileBlob => {
    return fileToText.convertToArray(fileBlob)
        .then(keyParser.parseGen2Dump)
        .then(multiPrivateKeysGen2 => {
            return claimMultiple(multiPrivateKeysGen2);
        });
};

/**
 * Promised implementation for an asynchronous delay
 *
 * @method delay
 * @protected
 * @since 0.0.1
 */
const delay = ms => {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve();
        }, ms);
    });
};

/**
 * Checks multiple Gen2 private keys for their balance that can be claimed.
 * Will return a Promise which resolves with on Object like this:
 *
 * {
 *     totalAmount,
 *     balancedKeys
 * }
 *
 * Where totalAmount is the sum of the balance that can be claimed by the given privateGen2Keys
 * and balancedKeys is a subset of multiPrivateKeysGen2: only those keys that have a balance.
 *
 * @method getBalancedCoins
 * @param multiPrivateKeysGen2 {Array} List with Gen2 private keys, not hexadecimal
 * @return {Promise} Promise that resolves with an Object with 2 properties:<br>
 *                   totalAmount (String) total amount that can be claimed
 *                   balancedKeys (Array) a subset of multiPrivateKeysGen2: only those keys that have a balance
 * @since 0.0.1
 */
const getBalancedCoins = async multiPrivateKeysGen2 => {
    return {
        totalAmount: (multiPrivateKeysGen2.length * AMOUNT_PER_ADDRESS).toString(),
        balancedKeys: multiPrivateKeysGen2
    };
};

/**
 * Checks multiple Gen2 private keys for their balance that can be claimed.
 * Will return a Promise which resolves with on Object like this:
 *
 * {
 *     totalAmount,
 *     balancedKeys
 * }
 *
 * Where totalAmount is the sum of the balance that can be claimed by the given privateGen2Keys
 * and balancedKeys is a subset of multiPrivateKeysGen2: only those keys that have a balance.
 *
 * @method getBalancedCoinsByMnemonic
 * @param mnemonic {String} a Mnenomic Phrase of 24 words, divided by whitespaces
 * @param [extraPhrase] {String} an optional extra (password) phrase
 * @param account=0 {Number} wallet id that belongs with the mnemonic
 * @return {Promise} Promise that resolves with an Object with 2 properties:<br>
 *                   totalAmount (String) total amount that can be claimed
 *                   balancedKeys (Array) a subset of multiPrivateKeysGen2: only those keys that have a balance
 * @since 0.4.0
 */
const getBalancedCoinsByMnemonic = (mnemonic, extraPhrase, purpose, coinType, account=0) => Promise.resolve({
    totalAmount: (1500+(account*100)).toString(),
    balancedKeys: [
        'GmffUCsCrSKQXR9w6NiSYumpfjQbStDfktVC291sedaUmKcfSrej',
        'Gntav6R3Y9S5cLJcrXVa7JKv8wRzYUWP1hdooZZaUy3es6acmf2t',
        'GiCfXu63nNsZwynV8QqxHgCLxfgXBiYFiqGgD2DoVaqUVCEFLsFW'
    ]
});

/**
 * Retrieves the balance that can be claimed for a single Gen2 private keys.
 *
 * @method getCoinBalance
 * @param privateKeysGen2 {String} Gen2 private key
 * @return {Promise} Promise which resolves with the balance that can be claimed
 * @since 0.2.0
 */
const getCoinBalance = () => Promise.resolve(AMOUNT_PER_ADDRESS.toString());

/**
 * Generates a hash out of a public Gen3 address.
 * The hash is generated by the smart contract.
 *
 * @method hashToSign
 * @param addressGen3 {String} array containing simple types
 * @return {Promise} resolves with a hash, including '0x'
 * @since 0.0.1
 */
const hashToSign = addressGen3 => {
    const smartcontract = new web3.eth.Contract(abi, smartContractAddress);
    if (addressGen3.substr(0, 2)!=='0x') {
        addressGen3 = '0x' + addressGen3;
    }
    return smartcontract.methods.hashToSign(addressGen3).call();
};

/**
 * Verifies if a Gen2 private key has NRG balance that can be claimed for the Gen3 networkAddress and retrieves the balance that can be claimed.
 *
 * @method verifyClaim
 * @return {String} amount of balance that can be claimed
 * @since 0.0.1
 */
const verifyClaim = () => Promise.resolve('1000');

/**
 * VerifyClaims multiple Gen2 private keys retrieves the balance that can be claimed.
 * A callback function can be used to track the progresss: it will be invoked after every single coin verification with the completed-percentage as only argument.
 *
 * @method verifyClaimMultiple
 * @param multiPrivateKeysGen2 {Array} List with Gen2 private keys, not hexadecimal
 * @param [addressGen3] {String} Hexadecimal Gen3 public key, may be prepended with '0x', but not required
 * @param [cb] {Function} callback function that recieves 1 argument: `percentage`. Will be invoked everytime a Gen2 private key is claimed.
 * @return {Promise} Promise hold the total amount (String) that can be claimed
 * @since 0.0.1
 */
const verifyClaimMultiple = (multiPrivateKeysGen2, addressGen3, cb) => {
    // Be careful not to overload the browser's javascript runtime, because parsePrivateKey takes some resources.
    // Therefore we introduced a small delay, so that the browser stays responsive
    if (typeof addressGen3==='function') {
        // second argument was a cb function
        cb = addressGen3;
        addressGen3 = null;
    }
    // ask for energiCommon object only once, so it can be reused for every single call for `claim`:
    let totalAmount = web3.utils.toBN(0);
    const verifyClaimOne = async index => {
        let percentage;
        const total = multiPrivateKeysGen2.length;
        const amount = await verifyClaim();

        totalAmount = totalAmount.add(web3.utils.toBN(amount));
        index++;
        if (typeof cb==='function') {
            percentage = Math.round(100*index/total);
            // now invoke the callback function, to update the caller how much is processed:
            cb(percentage);
        }
        // If all keys have been processed, then return with `txResponses`,
        // otherwise return with a new promise wich will process the next key:
        return (index<total) ? verifyClaimOne(index) : totalAmount.toString();
    };
    if (!Array.isArray(multiPrivateKeysGen2)) {
        return Promise.reject(errorMessages.CLAIM_MULTIPLE_REQUIRES_ARRAY);
    }
    if (multiPrivateKeysGen2.length===0) {
        return Promise.reject(errorMessages.CLAIM_MULTIPLE_EMPTY_ARRAY);
    }
    return verifyClaimOne(0);
};

/**
 * VerifyClaims a Gen2 wallet dump and retrieves the balance that can be claimed.
 *
 * @method verifyClaimWalletDump
 * @param fileBlob {Blob} Fileblob, that comes from an html input[type="file"] Element: listen for the onChange event and retrieve inputnode.files[0] to get the fileBlob.
 * @param [addressGen3] {String} Hexadecimal Gen3 public key, may be prepended with '0x', but not required
 * @param [cb] {Function} callback function that recieves 1 argument: `percentage`. Will be invoked everytime a Gen2 private key is claimed.
 * @return {Promise} Promise hold the total amount (String) that can be claimed
 * @since 0.0.1
 */
const verifyClaimWalletDump = (fileBlob, addressGen3, cb) => {
    return fileToText.convertToArray(fileBlob)
        .then(keyParser.parseGen2Dump)
        .then(multiPrivateKeysGen2 => {
            return verifyClaimMultiple(multiPrivateKeysGen2, addressGen3, cb);
        });
};

module.exports = (web3Instance, options) => {
    if (options) {
        abi = options.abi || abi;
        keyParser = options.keyParser || keyParser;
        networkAddress = options.networkAddress || networkAddress;
        smartContractAddress = options.smartContractAddress || smartContractAddress;
        networkId = options.networkId || networkId;
        chainId = options.chainId || chainId;
        ecsign = options.ecsign || ecsign;
    }
    // check first if we have a valid web3 instance:
    if (web3Instance && web3Instance.utils && web3Instance.utils.unitMap && (web3Instance.utils.unitMap.wei==='1')) {
        web3 = web3Instance;
        return {
            claim,
            claimMultiple,
            claimWalletDump,
            getBalancedCoins,
            getCoinBalance,
            getBalancedCoinsByMnemonic,
            hashToSign,
            verifyClaim,
            verifyClaimMultiple,
            verifyClaimWalletDump
        };
    }
    console.error(errorMessages.INITIALIZE_WITH_WEB3);
};
